import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  BookOpenIcon,
  PlayIcon,
  StarIcon,
  ClockIcon,
  UserGroupIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  TrophyIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDownIcon,
  TagIcon,
  ShareIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedModule, setExpandedModule] = useState(null);

  // Mock course data - in real app this would come from API
  const course = {
    id: parseInt(courseId),
    title: 'Advanced Healthcare Management',
    instructor: {
      name: 'Dr. Sarah Wilson',
      title: 'Chief Medical Officer',
      image: '/images/instructor-sarah.jpg',
      bio: 'Dr. Wilson has over 15 years of experience in healthcare administration and has led digital transformation initiatives at major healthcare systems.',
      rating: 4.9,
      courses: 12,
      students: 15000
    },
    category: 'Healthcare Management',
    difficulty: 'Advanced',
    duration: '8 weeks',
    rating: 4.8,
    reviewCount: 247,
    students: 1523,
    price: 299,
    originalPrice: 399,
    description: 'This comprehensive course covers strategic planning, quality improvement, and leadership in healthcare organizations. You\'ll learn from real-world case studies and develop practical skills for managing healthcare teams and operations.',
    learningObjectives: [
      'Develop strategic thinking for healthcare organizations',
      'Master quality improvement methodologies',
      'Build effective healthcare leadership skills',
      'Understand regulatory compliance requirements',
      'Learn financial management in healthcare settings',
      'Implement change management strategies'
    ],
    prerequisites: [
      'Basic understanding of healthcare systems',
      'Management experience preferred (not required)',
      'Completion of Healthcare Fundamentals course (recommended)'
    ],
    skills: ['Strategic Planning', 'Quality Management', 'Healthcare Leadership', 'Risk Assessment', 'Change Management', 'Financial Planning'],
    certification: {
      name: 'Healthcare Management Professional Certificate',
      authority: 'Apollo Healthcare Academy',
      validFor: '2 years',
      ceuCredits: 24
    },
    modules: [
      {
        id: 1,
        title: 'Healthcare System Overview',
        duration: '1.5 hours',
        lessons: 6,
        completed: false,
        lessons_detail: [
          { id: 1, title: 'Introduction to Healthcare Systems', duration: '15 min', type: 'video' },
          { id: 2, title: 'Healthcare Stakeholders', duration: '12 min', type: 'video' },
          { id: 3, title: 'Regulatory Environment', duration: '18 min', type: 'video' },
          { id: 4, title: 'Quality Metrics Overview', duration: '20 min', type: 'reading' },
          { id: 5, title: 'Case Study: System Integration', duration: '15 min', type: 'case_study' },
          { id: 6, title: 'Module 1 Quiz', duration: '10 min', type: 'quiz' }
        ]
      },
      {
        id: 2,
        title: 'Strategic Planning & Vision',
        duration: '2 hours',
        lessons: 8,
        completed: false,
        lessons_detail: [
          { id: 7, title: 'Strategic Planning Framework', duration: '25 min', type: 'video' },
          { id: 8, title: 'SWOT Analysis in Healthcare', duration: '20 min', type: 'interactive' },
          { id: 9, title: 'Mission & Vision Development', duration: '15 min', type: 'video' },
          { id: 10, title: 'Stakeholder Analysis', duration: '18 min', type: 'reading' },
          { id: 11, title: 'Resource Allocation Strategies', duration: '22 min', type: 'video' },
          { id: 12, title: 'Implementation Planning', duration: '15 min', type: 'video' },
          { id: 13, title: 'Group Discussion: Strategy Cases', duration: '20 min', type: 'discussion' },
          { id: 14, title: 'Strategic Planning Assignment', duration: '15 min', type: 'assignment' }
        ]
      },
      {
        id: 3,
        title: 'Quality Improvement Methods',
        duration: '1.8 hours',
        lessons: 7,
        completed: false,
        lessons_detail: [
          { id: 15, title: 'Introduction to Quality in Healthcare', duration: '18 min', type: 'video' },
          { id: 16, title: 'Lean Six Sigma Principles', duration: '25 min', type: 'video' },
          { id: 17, title: 'Root Cause Analysis', duration: '20 min', type: 'interactive' },
          { id: 18, title: 'Performance Metrics & KPIs', duration: '15 min', type: 'reading' },
          { id: 19, title: 'Continuous Improvement Cycles', duration: '12 min', type: 'video' },
          { id: 20, title: 'Quality Improvement Project', duration: '25 min', type: 'project' },
          { id: 21, title: 'Module 3 Assessment', duration: '15 min', type: 'quiz' }
        ]
      },
      {
        id: 4,
        title: 'Healthcare Leadership',
        duration: '2.2 hours',
        lessons: 9,
        completed: false,
        lessons_detail: [
          { id: 22, title: 'Leadership Styles in Healthcare', duration: '20 min', type: 'video' },
          { id: 23, title: 'Team Building & Management', duration: '18 min', type: 'video' },
          { id: 24, title: 'Communication Strategies', duration: '15 min', type: 'interactive' },
          { id: 25, title: 'Conflict Resolution', duration: '22 min', type: 'video' },
          { id: 26, title: 'Change Management', duration: '25 min', type: 'reading' },
          { id: 27, title: 'Motivating Healthcare Teams', duration: '16 min', type: 'video' },
          { id: 28, title: 'Leadership Ethics', duration: '14 min', type: 'video' },
          { id: 29, title: 'Case Study: Leadership Crisis', duration: '20 min', type: 'case_study' },
          { id: 30, title: 'Leadership Skills Assessment', duration: '18 min', type: 'assessment' }
        ]
      },
      {
        id: 5,
        title: 'Financial Management',
        duration: '1.7 hours',
        lessons: 6,
        completed: false,
        lessons_detail: [
          { id: 31, title: 'Healthcare Finance Basics', duration: '20 min', type: 'video' },
          { id: 32, title: 'Budgeting & Forecasting', duration: '25 min', type: 'video' },
          { id: 33, title: 'Cost-Benefit Analysis', duration: '18 min', type: 'interactive' },
          { id: 34, title: 'Revenue Cycle Management', duration: '22 min', type: 'reading' },
          { id: 35, title: 'Financial Performance Metrics', duration: '15 min', type: 'video' },
          { id: 36, title: 'Budget Planning Exercise', duration: '12 min', type: 'exercise' }
        ]
      },
      {
        id: 6,
        title: 'Risk Management & Compliance',
        duration: '1.5 hours',
        lessons: 5,
        completed: false,
        lessons_detail: [
          { id: 37, title: 'Healthcare Risk Assessment', duration: '20 min', type: 'video' },
          { id: 38, title: 'Regulatory Compliance', duration: '18 min', type: 'reading' },
          { id: 39, title: 'Patient Safety Protocols', duration: '22 min', type: 'video' },
          { id: 40, title: 'Crisis Management Planning', duration: '15 min', type: 'interactive' },
          { id: 41, title: 'Final Project Presentation', duration: '15 min', type: 'project' }
        ]
      }
    ],
    reviews: [
      {
        id: 1,
        user: 'Jennifer Martinez',
        role: 'Hospital Administrator',
        rating: 5,
        date: '2025-01-15',
        comment: 'Excellent course! The practical case studies really helped me understand complex healthcare management concepts.'
      },
      {
        id: 2,
        user: 'Dr. Michael Chang',
        role: 'Department Head',
        rating: 5,
        date: '2025-01-10',
        comment: 'Dr. Wilson is an outstanding instructor. The course content is current and highly relevant to real-world challenges.'
      },
      {
        id: 3,
        user: 'Lisa Thompson',
        role: 'Quality Manager',
        rating: 4,
        date: '2025-01-08',
        comment: 'Great course with comprehensive coverage of healthcare management topics. Would recommend to peers.'
      }
    ],
    relatedCourses: [
      {
        id: 2,
        title: 'Medical Ethics and Patient Care',
        instructor: 'Prof. Michael Chen',
        rating: 4.9,
        price: 199
      },
      {
        id: 5,
        title: 'Healthcare Data Analytics',
        instructor: 'Prof. Anna Kumar',
        rating: 4.8,
        price: 279
      }
    ]
  };

  useEffect(() => {
    // Simulate loading and check enrollment status
    setTimeout(() => {
      setIsLoading(false);
      // Check if user is already enrolled (mock data)
      setIsEnrolled(Math.random() > 0.7); // 30% chance of being enrolled
      setIsFavorited(Math.random() > 0.8); // 20% chance of being favorited
    }, 800);
  }, [courseId]);

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return PlayIcon;
      case 'reading': return BookOpenIcon;
      case 'quiz': return DocumentTextIcon;
      case 'assignment': return DocumentTextIcon;
      case 'project': return TrophyIcon;
      case 'interactive': return StarIcon;
      case 'case_study': return BookOpenIcon;
      case 'discussion': return UserGroupIcon;
      case 'assessment': return CheckCircleIcon;
      case 'exercise': return DocumentTextIcon;
      default: return BookOpenIcon;
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleEnroll = () => {
    setIsEnrolled(true);
    // In real app, this would make an API call
  };

  const toggleFavorite = () => {
    setIsFavorited(!isFavorited);
    // In real app, this would make an API call
  };

  const toggleModule = (moduleId) => {
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-apollo-gray">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Breadcrumb */}
        <div className="flex items-center text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          <button 
            onClick={() => navigate('/training/catalog')}
            className="hover:text-apollo-accent transition-colors duration-200 flex items-center"
          >
            <ArrowLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Course Catalog
          </button>
          <span className="mx-2">/</span>
          <span className="text-gray-900 truncate">{course.title}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Course Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6 sm:mb-8">
              {/* Course Image/Video */}
              <div className="relative h-48 sm:h-64 bg-gradient-to-br from-apollo-accent to-blue-600">
                <div className="absolute inset-0 flex items-center justify-center">
                  <PlayIcon className="h-16 w-16 sm:h-20 sm:w-20 text-white opacity-50 hover:opacity-75 cursor-pointer transition-opacity duration-200" />
                </div>
                
                {/* Course Badges */}
                <div className="absolute top-2 sm:top-4 left-2 sm:left-4 flex flex-wrap gap-1 sm:gap-2">
                  <span className="bg-yellow-500 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                    Featured
                  </span>
                  <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium ${getDifficultyColor(course.difficulty)}`}>
                    {course.difficulty}
                  </span>
                </div>

                {/* Actions */}
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 flex space-x-1 sm:space-x-2">
                  <button
                    onClick={toggleFavorite}
                    className="p-1.5 sm:p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
                  >
                    {isFavorited ? (
                      <HeartIconSolid className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
                    ) : (
                      <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    )}
                  </button>
                  <button className="p-1.5 sm:p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200">
                    <ShareIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Course Info */}
              <div className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                      {course.title}
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">
                      {course.description}
                    </p>
                  </div>
                </div>

                {/* Course Stats */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 lg:space-x-6 space-y-2 sm:space-y-0 mb-3 sm:mb-0">
                    <div className="flex items-center">
                      <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400 mr-1" />
                      <span className="font-medium">{course.rating}</span>
                      <span className="ml-1 hidden sm:inline">({course.reviewCount} reviews)</span>
                    </div>
                    
                    <div className="flex items-center">
                      <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      <span className="hidden sm:inline">{course.students.toLocaleString()} students</span>
                      <span className="sm:hidden">{(course.students / 1000).toFixed(1)}k students</span>
                    </div>
                    
                    <div className="flex items-center">
                      <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                      <span>{course.duration}</span>
                    </div>
                  </div>
                  
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium self-start sm:self-auto">
                    {course.category}
                  </span>
                </div>

                {/* Skills Tags */}
                <div className="mb-4 sm:mb-6">
                  <h3 className="text-sm font-medium text-gray-900 mb-2 sm:mb-3">Skills you'll gain:</h3>
                  <div className="flex flex-wrap gap-1 sm:gap-2">
                    {course.skills.map((skill, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm flex items-center">
                        <TagIcon className="h-3 w-3 mr-1" />
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Course Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="flex space-x-4 sm:space-x-6 lg:space-x-8 px-4 sm:px-6 overflow-x-auto">
                  {['overview', 'curriculum', 'instructor', 'reviews'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm transition-colors duration-200 whitespace-nowrap ${
                        activeTab === tab
                          ? 'border-apollo-accent text-apollo-accent'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-4 sm:p-6">
                {activeTab === 'overview' && (
                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3">What you'll learn</h3>
                      <ul className="space-y-2">
                        {course.learningObjectives.map((objective, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm sm:text-base text-gray-700">{objective}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Prerequisites</h3>
                      <ul className="space-y-2">
                        {course.prerequisites.map((prereq, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                            <span className="text-gray-700">{prereq}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Certification</h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start">
                          <TrophyIcon className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
                          <div>
                            <h4 className="font-medium text-blue-900">{course.certification.name}</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Issued by {course.certification.authority} • Valid for {course.certification.validFor}
                            </p>
                            <p className="text-sm text-blue-600 mt-1">
                              {course.certification.ceuCredits} CEU Credits
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'curriculum' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900">Course Curriculum</h3>
                      <span className="text-sm text-gray-600">
                        {course.modules.length} modules • {course.modules.reduce((acc, module) => acc + module.lessons, 0)} lessons
                      </span>
                    </div>

                    {course.modules.map((module, index) => (
                      <div key={module.id} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleModule(module.id)}
                          className="w-full p-4 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center justify-between"
                        >
                          <div className="flex items-center">
                            <span className="bg-apollo-accent text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-medium mr-3">
                              {index + 1}
                            </span>
                            <div>
                              <h4 className="font-medium text-gray-900">{module.title}</h4>
                              <p className="text-sm text-gray-600">{module.lessons} lessons • {module.duration}</p>
                            </div>
                          </div>
                          <ChevronDownIcon className={`h-5 w-5 text-gray-400 transform transition-transform duration-200 ${expandedModule === module.id ? 'rotate-180' : ''}`} />
                        </button>

                        {expandedModule === module.id && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            {module.lessons_detail.map((lesson, lessonIndex) => {
                              const LessonIcon = getLessonIcon(lesson.type);
                              return (
                                <div key={lesson.id} className="p-4 border-b border-gray-100 last:border-b-0 flex items-center">
                                  <LessonIcon className="h-4 w-4 text-gray-400 mr-3" />
                                  <span className="flex-1 text-sm text-gray-700">{lesson.title}</span>
                                  <span className="text-xs text-gray-500">{lesson.duration}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'instructor' && (
                  <div>
                    <div className="flex items-start space-x-4 mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white text-2xl font-bold">
                          {course.instructor.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-gray-900">{course.instructor.name}</h3>
                        <p className="text-gray-600 mb-2">{course.instructor.title}</p>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                            <span>{course.instructor.rating} instructor rating</span>
                          </div>
                          <div className="flex items-center">
                            <BookOpenIcon className="h-4 w-4 mr-1" />
                            <span>{course.instructor.courses} courses</span>
                          </div>
                          <div className="flex items-center">
                            <UserGroupIcon className="h-4 w-4 mr-1" />
                            <span>{course.instructor.students.toLocaleString()} students</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-700">{course.instructor.bio}</p>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-900">Student Reviews</h3>
                      <div className="flex items-center">
                        <StarIcon className="h-5 w-5 text-yellow-400 mr-1" />
                        <span className="font-medium">{course.rating}</span>
                        <span className="text-gray-600 ml-1">({course.reviews.length} reviews)</span>
                      </div>
                    </div>

                    <div className="space-y-6">
                      {course.reviews.map((review) => (
                        <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-medium text-gray-900">{review.user}</h4>
                              <p className="text-sm text-gray-600">{review.role}</p>
                            </div>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <StarIcon
                                  key={star}
                                  className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
                                />
                              ))}
                              <span className="text-sm text-gray-600 ml-2">
                                {new Date(review.date).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-gray-700">{review.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Enrollment Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-2">
                  <span className="text-3xl font-bold text-gray-900">
                    ${course.price}
                  </span>
                  {course.originalPrice && (
                    <span className="text-lg text-gray-500 line-through ml-2">
                      ${course.originalPrice}
                    </span>
                  )}
                </div>
                {course.originalPrice && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm font-medium">
                    25% off limited time
                  </span>
                )}
              </div>

              {isEnrolled ? (
                <div className="space-y-4">
                  <button
                    onClick={() => navigate(`/training/course/${course.id}/learn`)}
                    className="w-full bg-apollo-accent text-white py-3 px-4 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center justify-center"
                  >
                    Continue Learning
                    <ArrowRightIcon className="h-4 w-4 ml-2" />
                  </button>
                  <div className="text-center">
                    <p className="text-sm text-green-600 font-medium">✓ You're enrolled in this course</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <button
                    onClick={handleEnroll}
                    className="w-full bg-apollo-accent text-white py-3 px-4 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200"
                  >
                    Enroll Now
                  </button>
                  <p className="text-xs text-gray-600 text-center">
                    30-day money-back guarantee
                  </p>
                </div>
              )}

              {/* Course Features */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">This course includes:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <PlayIcon className="h-4 w-4 mr-2" />
                    12 hours of video content
                  </li>
                  <li className="flex items-center">
                    <DocumentTextIcon className="h-4 w-4 mr-2" />
                    Downloadable resources
                  </li>
                  <li className="flex items-center">
                    <TrophyIcon className="h-4 w-4 mr-2" />
                    Certificate of completion
                  </li>
                  <li className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    Lifetime access
                  </li>
                </ul>
              </div>
            </div>

            {/* Related Courses */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Related Courses</h3>
              <div className="space-y-4">
                {course.relatedCourses.map((relatedCourse) => (
                  <div key={relatedCourse.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200 cursor-pointer">
                    <div className="w-12 h-12 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-lg flex items-center justify-center">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate">
                        {relatedCourse.title}
                      </h4>
                      <p className="text-xs text-gray-600">{relatedCourse.instructor}</p>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center">
                          <StarIcon className="h-3 w-3 text-yellow-400 mr-1" />
                          <span className="text-xs text-gray-600">{relatedCourse.rating}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">${relatedCourse.price}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CourseDetail;
